import { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';

import config from '../../config/config';
import { useBranding } from '../../context/BrandingContext';
import { useWebSocket } from '../../context/WebSocket/WebSocketProvider';
import ToastMessage from '../../shared-components/toast-message/ToastMessage';
import getFirstSubdomain from '../../shared-functions/DomiansplitFunction';
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from '../../shared-functions/LocalStorage';
import charger from '../../utils/api/charger';

import ChargingStage from './components/ChargingStage';
import ChargingStatus from './components/ChargingStatus';
import GenerateHyperPay from './components/hyperpay';
import Invoice from './components/Invoice';
import PaymentMethod from './components/PaymentMethod';

interface ConnectorInterface {
  _id: string;
  connector: string;
  connectorId: string | number;
  isReservationEnabled: boolean;
  status: string;
}

interface PricingDetailsProperties {
  currentPrice: string;
  dynamicPricing: {
    price: string;
    priceType: string;
    timings: {
      startDateUtc: string | null;
      endDateUtc: string | null;
    }[];
  }[];
}

interface DurationAmount {
  from: number;
  to: number;
  mode: 'grace' | 'flatRate' | 'perMinute';
  amount: number;
}

interface IdleFeePrices {
  durationAmountList: DurationAmount[];
  ceilingValue: number;
}

type StageStatus = {
  inProgress: boolean;
  done: boolean;
};

type FormStageState = {
  paymentDetails: StageStatus;
  charging: StageStatus;
  invoice: StageStatus;
};

type PaymentDetails = {
  paymentMethodId?: string; // The ID of the payment method
  last4?: string; // Last 4 digits of the card
  expiryMonth?: number; // Card expiration month
  expiryYear?: number; // Card expiration year
  funding?: string; // Card funding type (e.g., credit, debit, prepaid)
  brand?: string; // Card brand (e.g., Visa, Mastercard)
  cardHolderName: string; // Name of the cardholder
};

/**
 * Charger component
 * @returns
 */
function Charger() {
  const webSocket = useWebSocket();
  const domain = getFirstSubdomain();
  const { brandData } = useBranding();
  const currentTransactionId = getLocalStorageItem('guestToken');
  const [paymentDetails, setPaymentDetails] = useState<PaymentDetails>();
  const [chargingStatus, setChargingStatus] = useState<boolean>(false);
  const appVersion = getLocalStorageItem('appVersion') || '';

  const { deviceId } = useParams<{ deviceId: string }>(); // Extracts the ID from the URL

  const location = useLocation();
  const queryParameters = new URLSearchParams(location.search);
  const hyperPayId = queryParameters.get('id');
  const [transactionId, setTransactionId] = useState<string | null>(
    getLocalStorageItem('guestToken') || null,
  );

  const [connectors, setConnectors] = useState<ConnectorInterface[]>([]);
  const [skipChargingButton, setSkipChargingButton] = useState<boolean>(false);
  const [pricingDetails, setPricingDetails] = useState<PricingDetailsProperties>({
    currentPrice: '',
    dynamicPricing: [],
  });
  const [idleFeePrices, setIdleFeePrices] = useState<IdleFeePrices | null>(null);
  const [toastStatus, setToastStatus] = useState({
    openToast: false,
    showToastMessage: '',
  });
  const [formStage, setFormStage] = useState<FormStageState>({
    paymentDetails: {
      inProgress: true,
      done: false,
    },
    charging: {
      inProgress: false,
      done: false,
    },
    invoice: {
      inProgress: false,
      done: false,
    },
  });
  const [isSessionChecked, setIsSessionChecked] = useState(false);
  const domainParameter = queryParameters.get('domain');

  /**
   * Function to set the formStatus as charging session
   */
  const navigateToChargingSession = () => {
    setFormStage({
      paymentDetails: {
        inProgress: false,
        done: true,
      },
      charging: {
        inProgress: true,
        done: false,
      },
      invoice: {
        inProgress: false,
        done: false,
      },
    });
  };

  /**
   * Function to set the formStatus as charging session
   */
  const navigateToPaymentMethod = (removeQueryParameters?: boolean) => {
    if (removeQueryParameters) {
      const urlWithoutQuery = domainParameter
        ? `${window.location.origin}${window.location.pathname}?domain=${domainParameter}`
        : `${window.location.origin}${window.location.pathname}`;

      // Replace the current history entry without reloading the page
      window.history.replaceState({}, '', urlWithoutQuery);
    }

    setFormStage({
      paymentDetails: {
        inProgress: true,
        done: false,
      },
      charging: {
        inProgress: false,
        done: false,
      },
      invoice: {
        inProgress: false,
        done: false,
      },
    });
  };

  /**
   * Function to set the formStatus as charging session
   */
  const navigateToInvoice = () => {
    setFormStage({
      paymentDetails: {
        inProgress: false,
        done: true,
      },
      charging: {
        inProgress: false,
        done: true,
      },
      invoice: {
        inProgress: true,
        done: false,
      },
    });
  };

  /**
   * Fetch the list of connectors for the device
   */
  const getConnectors = async () => {
    try {
      const response = await charger.fetchOneDevicePoints(deviceId ?? '');
      const apiResponse = response.data;
      // Update connectorDetails
      setConnectors(
        apiResponse?.connectors?.map((connector: ConnectorInterface) => ({
          ...connector,
          connector: `Connector ${connector?.connectorId}`,
          isReservationEnabled: connector?.status === 'Available',
        })) || [],
      );
      // Update payment button
      setSkipChargingButton(apiResponse?.skipCharging || false);
      // Update pricing and current pricing details
      setPricingDetails({
        currentPrice: apiResponse?.currentPrice || '',
        dynamicPricing: apiResponse?.dynamicPricing?.length
          ? apiResponse.dynamicPricing.map((pricing: any) => ({
              price: pricing.price,
              priceType: pricing.pricingType,
              timings: pricing?.timings,
            }))
          : [],
      });
      setIdleFeePrices(apiResponse.penaltyDetails);
    } catch (error: any) {
      setToastStatus({
        openToast: true,
        showToastMessage: error?.response?.data?.message,
      });
    }
  };

  /**
   * Get the guest token
   */
  const getGuestToken = async () => {
    if (!currentTransactionId) {
      const response = await charger.generateTempToken();
      setLocalStorageItem('guestToken', response?.data?.guestToken);
    }
  };

  useEffect(() => {
    if (domainParameter) {
      setLocalStorageItem('domain', domainParameter);
    } else {
      localStorage.removeItem('domain'); // ✅ remove when not present
    }
  }, [domainParameter]);

  /**
   * -------------------------
   * LocalStorage values
   * ------------------------
   */
  const isWaiting = getLocalStorageItem('waitingForPlug');
  const startTime = getLocalStorageItem('plugStartTime');

  /**
   * -------------------------
   * Time calculation
   * ------------------------
   */
  const elapsedSeconds = startTime ? Math.floor((Date.now() - Number(startTime)) / 1000) : 0;

  const isExpired = elapsedSeconds >= 60;

  /**
   * -------------------------
   * Cleanup expired waiting
   * ------------------------
   */
  useEffect(() => {
    if (isWaiting && isExpired) {
      localStorage.removeItem('waitingForPlug');
      localStorage.removeItem('plugStartTime');
    }
  }, [isWaiting, isExpired]);
 
  /**
   *check Status Charger
   */
  const checkStatusCharger = async () => {
    try {
      const response = await charger.refreshSession({
        transactionId,
        chargingPointSn: deviceId,
      });

      const { data } = response || {};
      if (!data?.status  || (data?.status !== 'finished' || data?.status !== 'inProgress' || data?.status !== 'onHold')) {
        setIsSessionChecked(true);
      }
      // If data or status is empty → return
      if (!data?.status) {
        return;
      }

      switch (data.status) {
        case 'onHold':
          setLocalStorageItem('paidAmount', data?.paidAmount || 0);
          setLocalStorageItem('totalKwhConsumer', data?.powerConsumedInKwh || 0);
          setChargingStatus(false);
          navigateToChargingSession();
          break;

        case 'inProgress':
          // ⚡ Charging started
          removeLocalStorageItem('waitingForPlug');
          removeLocalStorageItem('plugStartTime');
          setTransactionId(transactionId);
          setChargingStatus(true);
          navigateToChargingSession();
          break;

        case 'finished':
          setLocalStorageItem('paidAmount', data?.paidAmount || 0);
          setLocalStorageItem('totalKwhConsumer', data?.powerConsumedInKwh || 0);
          navigateToInvoice();
          break;

        default:
          navigateToPaymentMethod(true);
      }

      if (isWaiting && !isExpired) {
        navigateToChargingSession();
      }
    } catch (error: any) {
      setToastStatus({
        openToast: true,
        showToastMessage: error?.response?.data?.message || 'Session error',
      });
    }
  };

  useEffect(() => {
    if (deviceId && transactionId) {
      checkStatusCharger();
    }
  }, [deviceId, transactionId]);

  useEffect(() => {
    getGuestToken();
    if (deviceId) {
      getConnectors();
    }
    if (hyperPayId) {
      webSocket?.generateTemporaryToken();
      navigateToChargingSession();
    }
  }, [hyperPayId]);

  const allowFreeCharging =
    skipChargingButton ||
    (domain && brandData?.guestPaymentMethod
      ? brandData?.guestPaymentMethod
      : config.guestPaymentMode) === 'free';
  useEffect(() => {
    if (isSessionChecked && allowFreeCharging && !formStage.charging.inProgress) {
      navigateToChargingSession();
    }
  }, [isSessionChecked,allowFreeCharging]);
  return (
    <div className="charger-wrap">
      {' '}
      <ToastMessage
        open={toastStatus.openToast}
        onClose={() =>
          setToastStatus({
            openToast: false,
            showToastMessage: '',
          })
        }
        type="error"
        message={toastStatus.showToastMessage || 'Something went wrong'}
        onCloseTimer={6000}
      />
      <ChargingStage formStage={formStage} />
      {formStage.paymentDetails.inProgress &&
        (domain && brandData?.guestPaymentMethod
          ? brandData?.guestPaymentMethod
          : config.guestPaymentMode) === 'stripe' && (
          <PaymentMethod
            navigateToChargingSession={navigateToChargingSession}
            setPaymentDetails={setPaymentDetails}
            navigateToInvoice={navigateToInvoice}
            skipChargingButton={skipChargingButton}
            pricingDetails={pricingDetails}
            idleFeePrices={idleFeePrices}
            appVersion={appVersion}
          />
        )}
      {formStage.paymentDetails.inProgress &&
        (domain && brandData?.guestPaymentMethod
          ? brandData?.guestPaymentMethod
          : config.guestPaymentMode) === 'hyperpay' && (
          <GenerateHyperPay pricingDetails={pricingDetails} idleFeePrices={idleFeePrices} />
        )}
      {formStage.charging.inProgress && (
        <ChargingStatus
          paymentDetails={paymentDetails}
          navigateToPaymentMethod={navigateToPaymentMethod}
          navigateToInvoice={navigateToInvoice}
          chargingStatus={chargingStatus}
          connectors={connectors}
          skipChargingButton={skipChargingButton}
          currentPrice={pricingDetails.currentPrice}
          appVersion={appVersion}
        />
      )}
      {formStage.invoice.inProgress && <Invoice />}
    </div>
  );
}

export default Charger;
